import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);

  const saveAuth = (tokenVal, userVal) => {
    localStorage.setItem("auth_token", tokenVal);
    setToken(tokenVal);
    setUser(userVal);
    // Sync community user_id for backward compatibility
    if (userVal?.id) localStorage.setItem("community_user_id", userVal.id);
  };

  const logout = useCallback(() => {
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
  }, []);

  // Verify token on mount
  useEffect(() => {
    const verify = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          if (data.user?.id) localStorage.setItem("community_user_id", data.user.id);
        } else {
          logout();
        }
      } catch {
        logout();
      }
      setLoading(false);
    };
    verify();
  }, [token, logout]);

  const register = async (name, email, password, pin, avatarUrl, phoneNumber) => {
    // Check for referral code from share links
    const referralCode = localStorage.getItem("referral_code") || undefined;
    const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password, pin, avatar_url: avatarUrl, referral_code: referralCode, phone_number: phoneNumber || undefined }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Registration failed");
    saveAuth(data.token, data.user);
    // Clear used referral code
    localStorage.removeItem("referral_code");
    return data;
  };

  const login = async (email, password) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Login failed");
    saveAuth(data.token, data.user);
    return data;
  };

  const loginWithPin = async (email, pin) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login-pin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, pin }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "PIN login failed");
    saveAuth(data.token, data.user);
    return data;
  };

  const updateProfile = async (updates) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
    if (res.ok) {
      const data = await res.json();
      setUser(data.user);
    }
  };

  const patchUser = useCallback((fields) => {
    setUser((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  // Compute premium access from user subscription data
  const hasPremiumAccess = useMemo(() => {
    if (!user?.subscription) return false;
    const { plan, trial_end } = user.subscription;
    if (plan === "monthly" || plan === "annual") return true;
    if (plan === "trial" && trial_end) {
      return new Date(trial_end) > new Date();
    }
    return false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, register, login, loginWithPin, logout, updateProfile, patchUser, hasPremiumAccess }}>
      {children}
    </AuthContext.Provider>
  );
};
